import { generateHeightmap, smoothHeightmap } from "./heightmap";
import { applyTalusErosion } from "./erosion";
import { addPonds, carveRiverWithBanks, carveWaterfalls, generateRiverMask } from "./rivers";
import { generateBiomes } from "./biomes";
import { BIOME_RULES } from "./biomeRules";
import { applyCoastRule } from "./coast";
import { placeTrees } from "./trees";
import { placeDecorations } from "./decorations";
import { carveCaves } from "./caves";
import { placeStructures } from "./structures";
import { placeNetherPortal, seedHasPortal } from "./portal";
import { buildOverworldVoxels } from "./terrainVoxels";
import { BIOME, type BiomeMap, type Biome } from "../types";
import {
    GRID_SIZE,
    WORLD_HEIGHT,
    RIVER_OCCURRENCE_MOD,
    SMOOTHING_PASSES,
    CAVE_ENABLED,
    STRUCTURES_ENABLED,
} from "../config";

export interface BuildWorldOptions {
    forceBiome?: Biome;
    size?: number;
    height?: number;
}

export function buildWorld(seed: number, opts: BuildWorldOptions = {}) {
    const size = opts.size ?? GRID_SIZE;
    const worldHeight = opts.height ?? WORLD_HEIGHT;
    let heights = generateHeightmap(seed, size);
    heights = applyTalusErosion(heights);
    heights = smoothHeightmap(heights, SMOOTHING_PASSES);

    const biomes: BiomeMap = generateBiomes(seed, size);

    if (opts?.forceBiome) {
        for (let x = 0; x < size; x++) {
            for (let z = 0; z < size; z++) {
                biomes.biome[x][z] = opts.forceBiome;
            }
        }
    }

    // Rivers depend on the primary biome (cell 0,0 is always primary).
    const primaryBiome: Biome = biomes.biome[0][0];
    const primaryRules = BIOME_RULES[primaryBiome];
    const isSwamp = primaryBiome === BIOME.Swamp;
    const seedSuggestsRiver = seed % RIVER_OCCURRENCE_MOD === 0;
    // Swamp is always wet: flood it regardless of seed.
    const hasRiver = (isSwamp || seedSuggestsRiver) && primaryRules.allowRivers;
    let riverMask: boolean[][] | undefined;
    if (hasRiver) {
        const halfThickness = isSwamp ? 3 : undefined;
        riverMask = generateRiverMask(seed, size, halfThickness);
        if (isSwamp) addPonds(riverMask, seed, size, 0.35);
        heights = carveRiverWithBanks(heights, riverMask);
    }

    const world = buildOverworldVoxels(heights, biomes, riverMask, seed, worldHeight);

    if (CAVE_ENABLED) carveCaves(world, heights, seed);

    applyCoastRule(world, heights);
    if (riverMask && primaryRules.riverFluid) {
        carveWaterfalls(world, heights, riverMask, primaryRules.riverFluid);
    }
    const structure = STRUCTURES_ENABLED ? placeStructures(world, heights, biomes, seed) : null;
    placeTrees(world, heights, biomes, seed);
    if (seedHasPortal(seed)) placeNetherPortal(world, heights, seed);
    placeDecorations(world, heights, biomes, seed);

    return { world, heights, biomes, structure };
}
